import { type DOMProps, Flex, Image } from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import type React from "react";
import type { RefAttributes } from "react";
import type { WithMetadata } from "../../../pages";
import { ChatLink } from "./ChatLink";
import { DEFAULT_AVATAR } from "./AvatarData";
import "./ChatBox.css";

export type ChatBoxElement = HTMLDivElement;

export interface ChatBoxProps extends DOMProps, RefAttributes<ChatBoxElement> {
  /** The message content to display in the chat box */
  children: React.ReactNode;
  /** Horizontal position of the chat box */
  position?: "left" | "center" | "right";
  /** Custom class name for setting specific CSS */
  className?: string;
  /** Custom element type to render as */
  elementType?: React.ElementType;
  /** Custom avatar image source */
  avatarSrc?: string;
  /** Custom avatar alt text */
  avatarAlt?: string;
}

const ChatBoxComponent = ({ ref, ...props }: WithMetadata<ChatBoxProps>) => {
  const {
    elementType: Element = "div",
    children,
    componentName,
    position = "center",
    avatarSrc = DEFAULT_AVATAR,
    avatarAlt = "",
    ...otherProps
  } = props;

  const classes = classNames(
    "mobius",
    "mobius-chatbox",
    `mobius-chatbox--${position}`,
    otherProps.className,
  );
  const { variant } = otherProps;

  return (
    <Element
      ref={ref}
      {...otherProps}
      {...(componentName ? { "data-component": componentName } : {})}
      {...(variant ? { "data-variant": variant } : {})}
      className={classes}
    >
      <div className="mobius-chatbox__grid">
        <div className="mobius-chatbox__avatar-container">
          <Image
            className="mobius-chatbox__avatar"
            src={avatarSrc}
            alt={avatarAlt}
          />
        </div>
        <div className="mobius-chatbox__message">
          <Flex
            flexDirection="column"
            gap="xs"
            className="mobius-chatbox__inner"
          >
            {children}
          </Flex>
        </div>
      </div>
    </Element>
  );
};

ChatBoxComponent.displayName = "ChatBox";

// Create the ChatBox component with sub-components
const ChatBox = Object.assign(ChatBoxComponent, {
  ChatLink,
});

export { ChatBox };
